import { channelTypeToName } from "../../const";
import BaseNodeModel from "../BaseNodeModel";

export default class ExitNodeModel extends BaseNodeModel {
    /**
     * Module exit node model
     * @module ExitNodeModel
     * @param {string} nodeName Node display name
     * @param {number} x Node x coordinate on canvas
     * @param {number} y Node y coordinate on canvas
     * @param {Object} props Pre-defined node properties; if non-null, apply values to node properties
     * @param {string} id If non-null, use this ID as the node ID
     */
    constructor(nodeName = "UNTITLED_NODE", x = 0, y = 0, props = null, id = null) {
        super(nodeName, x, y, null, id);
        this.props.nodeTypeName = "Module Exit";
        this.props.colorClass = "purple";
        this.props.iconClass = "log-out";
        this.DEFAULT_COLOR_CLASS = "purple";
        this.applyProps(props);
    }

    setConfig(key, value) {
        switch(key) {
            case "exitHandling": {
                if(value === "CHANNEL_END"){
                    this.props.config.exitComponent ??= {};
                    const {
                        channels,
                        defaultChannel
                    } = window.apm.serialized.rootSettings;
                    const { channelType } = this.props.config.exitComponent;

                    // When channel end is selected in exit handling by default add channel type as voice.
                    if (!channelType || !(channelType in channels)) {
                        this.props.config.exitComponent.channelType = channelTypeToName[defaultChannel];
                    }
                } else {
                    delete this.props.config.exitComponent.channelType;
                }

                // Refresh property panel when changing what function the node is using
                document.dispatchEvent(new CustomEvent("ev-property-refresh"));
                break;
            }
            case "type": {
                if (this.props.config[key] !== value) {
                    this.props.config.outputs[0].type = value;
                    this.props.lastLocalChange = window.apm.timestamp;
                }
                break;
            }
            case "value": {
                this.props.config.outputs[0].value = value;
                this.props.config.outputs[0].argumentName = null;
                this.props.config.outputs[0].varName = null;
                this.props.config.outputs[0].intent = null;
                delete this.props.config.intent;
                delete this.props.config.varName;
                this.props.lastLocalChange = window.apm.timestamp;
                break;
            }
            case "varName": {
                this.props.config.outputs[0].argumentName = value;
                this.props.config.outputs[0].varName = value;
                this.props.config.outputs[0].value = null;
                this.props.config.outputs[0].intent = null;
                delete this.props.config.intent;
                delete this.props.config.value;
                this.props.lastLocalChange = window.apm.timestamp;
                break;
            }
            case "intent": {
                this.props.config.outputs[0].intent = value;
                this.props.config.outputs[0].value = null;
                this.props.config.outputs[0].argumentName = null;
                this.props.config.outputs[0].varName = null;
                delete this.props.config.value;
                delete this.props.config.varName;
                this.props.lastLocalChange = window.apm.timestamp;
                break;
            }
        }

        super.setConfig(key, value);
        document.dispatchEvent(new CustomEvent("ev-property-refresh"));
    }

    getConfig(key) {
        if (key === "exitHandling" && !this.props.config.hasOwnProperty("exitHandling")) {
            //this.props.config.exitHandling = "HANGUP";
            console.error("exit node missing exitHandling")
            this.props.config.exitHandling = "ENDPOINT";
        }
        return super.getConfig(key);
    }
    /**
     * 
     * @returns options for exit handling drop down based on simultaneous feature flag
     * TODO: remove if condition when simultaneous is removed from feature flag
     */
    getExitHandlingOptions(){
        let options = [
            {name: "Channel End", value: "CHANNEL_END"},
            {name: "End Point", value: "ENDPOINT"},
            {name: "Hangup", value: "HANGUP"} ,
            {name:"Return to Previous Input", value: "RETURN_TO_PREVIOUS_INPUT"},
            {name:"Return to Previous Module", value: "RETURN_TO_PREVIOUS_MODULE"},
            {name:"Return to Root", value: "RETURN_TO_ROOT_MODULE"},
            {name: "Subdialog Return", value: "SUBDIALOG_RETURN"} ,
            {name: "Transfer", value: "TRANSFER"},
            {name: "Hand Off", value: "HAND_OFF"}
        ];

        return options;
    }

    getConfigViews(){
        const exitHandling = this.getConfig("exitHandling") ? this.getConfig("exitHandling") : "ENDPOINT";
        let configViews = [];
        var outputs = this.getConfig("outputs") ? this.getConfig("outputs") : [{ "argumentName": null, "type": "VALUE", "value": null, "varName": null, "intent": null }]; 
        
        if (outputs.length > 0) {
            this.props.config.type = this.props.config.type ? this.props.config.type : outputs[0].type;
            configViews.push({
                type: "Dropdown", key: "type", label: "Module Output", optionName: "Select a type...",
                options: window.apm.knowledgeBase.getNodeExitTypes()
            });
            switch (outputs[0].type) {
                case "VALUE":
                    this.props.config.value = this.props.config.value? this.props.config.value: outputs[0].value;
                    const value = this.getConfig("value") ? this.getConfig("value") : outputs[0].value;
                    configViews.push({
                        type: "Input",
                        key: "value",
                        value: value});
                    break;
                case "VARIABLE":
                    const varName = this.getConfig("varName") ? this.getConfig("varName") : outputs[0].varName;
                    this.props.config.varName = this.props.config.varName? this.props.config.varName: outputs[0].varName;
                    configViews.push({
                        type: "Input",
                        key: "varName",
                        value: varName,
                        suggestion: "variable"});
                    break;
                case "INTENT":
                    this.props.config.intent = this.props.config.intent? this.props.config.intent: outputs[0].intent;
                    configViews.push({
                        type: "Dropdown",
                        key: "intent",
                        optionName: "Select Intent",
                        options: window.apm.knowledgeBase.getIntents()});
                    break;
        }
    }
        return [
            ...this.getConfigViewNameField(),
            ...[
                
                ...configViews,
                
            ],
            ...[
                {type: "hr"},
                {
                    type: "Dropdown",
                    key: "exitHandling",
                    label: this.getPropLabel("exit_handling"),
                    optionName: this.getPropLabel("exit_handling_opt"),
                    options : this.getExitHandlingOptions()
                   
                },
                {
                    type: "ExitComponent",
                    key: "exitComponent",
                    exitHandling: exitHandling,
                    readOnly: this.props.isCommonModule
                },
                {type: "hr"},
                {
                    type: "Textarea",
                    label: this.getPropLabel("note"),
                    key: "note"
                }
            ]
        ];
    }
}
